import { __assign, __read, __spread, __values } from "tslib";
/* eslint-disable no-continue */
import * as u from '@virtuoso.dev/urx';
import { arrayToRanges, empty, findMaxKeyValue, insert, newTree, rangesWithin, remove, walk, } from './AATree';
import { loggerSystem, LogLevel } from './loggerSystem';
import * as arrayBinarySearch from './utils/binaryArraySearch';
import { correctItemSize } from './utils/correctItemSize';
function rangeIncludes(refRange) {
    var size = refRange.size, startIndex = refRange.startIndex, endIndex = refRange.endIndex;
    return function (range) {
        return (range.start === startIndex &&
            (range.end === endIndex || range.end === Infinity) &&
            range.value === size);
    };
}
export function insertRanges(sizeTree, ranges) {
    var e_1, _a, e_2, _b;
    var syncStart = empty(sizeTree) ? 0 : Infinity;
    try {
        for (var ranges_1 = __values(ranges), ranges_1_1 = ranges_1.next(); !ranges_1_1.done; ranges_1_1 = ranges_1.next()) {
            var range = ranges_1_1.value;
            var size = range.size, startIndex = range.startIndex, endIndex = range.endIndex;
            syncStart = Math.min(syncStart, startIndex);
            if (empty(sizeTree)) {
                sizeTree = insert(sizeTree, 0, size);
                continue;
            }
            // extend the range in both directions, so that we can get adjacent neighbours.
            // if the previous / next ones have the same value as the one we are about to insert,
            // we 'merge' them.
            var overlappingRanges = rangesWithin(sizeTree, startIndex - 1, endIndex + 1);
            if (overlappingRanges.some(rangeIncludes(range))) {
                continue;
            }
            var firstPassDone = false;
            var shouldInsert = false;
            try {
                for (var overlappingRanges_1 = (e_2 = void 0, __values(overlappingRanges)), overlappingRanges_1_1 = overlappingRanges_1.next(); !overlappingRanges_1_1.done; overlappingRanges_1_1 = overlappingRanges_1.next()) {
                    var _c = overlappingRanges_1_1.value, rangeStart = _c.start, rangeEnd = _c.end, rangeValue = _c.value;
                    // previous range
                    if (!firstPassDone) {
                        shouldInsert = rangeValue !== size;
                        firstPassDone = true;
                    }
                    // remove the range if it starts within the new range OR if
                    // it has the same value as it, in order to perform a merge
                    else if (endIndex >= rangeStart || size === rangeValue) {
                        sizeTree = remove(sizeTree, rangeStart);
                    }
                    // next range
                    if (rangeEnd > endIndex && endIndex >= rangeStart) {
                        if (rangeValue !== size) {
                            sizeTree = insert(sizeTree, endIndex + 1, rangeValue);
                        }
                    }
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (overlappingRanges_1_1 && !overlappingRanges_1_1.done && (_b = overlappingRanges_1.return)) _b.call(overlappingRanges_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
            if (shouldInsert) {
                sizeTree = insert(sizeTree, startIndex, size);
            }
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (ranges_1_1 && !ranges_1_1.done && (_a = ranges_1.return)) _a.call(ranges_1);
        }
        finally { if (e_1) throw e_1.error; }
    }
    return [sizeTree, syncStart];
}
export function initialSizeState() {
    return {
        offsetTree: [],
        sizeTree: newTree(),
        groupOffsetTree: newTree(),
        lastIndex: 0,
        lastOffset: 0,
        lastSize: 0,
        groupIndices: [],
    };
}
export function indexComparator(_a, index) {
    var itemIndex = _a.index;
    return index === itemIndex ? 0 : index < itemIndex ? -1 : 1;
}
export function offsetComparator(_a, offset) {
    var itemOffset = _a.offset;
    return offset === itemOffset ? 0 : offset < itemOffset ? -1 : 1;
}
function offsetPointParser(point) {
    return { index: point.index, value: point };
}
export function rangesWithinOffsets(tree, startOffset, endOffset, minStartIndex) {
    if (minStartIndex === void 0) { minStartIndex = 0; }
    if (minStartIndex > 0) {
        startOffset = Math.max(startOffset, arrayBinarySearch.findClosestSmallerOrEqual(tree, minStartIndex, indexComparator).offset);
    }
    return arrayToRanges(arrayBinarySearch.findRange(tree, startOffset, endOffset, offsetComparator), offsetPointParser);
}
function createOffsetTree(prevOffsetTree, syncStart, sizeTree) {
    var e_3, _a;
    var offsetTree = prevOffsetTree;
    var prevIndex = 0;
    var prevSize = 0;
    var prevOffset = 0;
    var startIndex = 0;
    if (syncStart !== 0) {
        startIndex = arrayBinarySearch.findIndexOfClosestSmallerOrEqual(offsetTree, syncStart - 1, indexComparator);
        var offsetInfo = offsetTree[startIndex];
        prevOffset = offsetInfo.offset;
        var kv = findMaxKeyValue(sizeTree, syncStart - 1);
        prevIndex = kv[0];
        prevSize = kv[1];
        if (offsetTree.length &&
            offsetTree[startIndex].size === findMaxKeyValue(sizeTree, syncStart)[1]) {
            startIndex -= 1;
        }
        offsetTree = offsetTree.slice(0, startIndex + 1);
    }
    else {
        offsetTree = [];
    }
    try {
        for (var _b = __values(rangesWithin(sizeTree, syncStart, Infinity)), _c = _b.next(); !_c.done; _c = _b.next()) {
            var _d = _c.value, startIndex_1 = _d.start, value = _d.value;
            var aOffset = (startIndex_1 - prevIndex) * prevSize + prevOffset;
            offsetTree.push({
                offset: aOffset,
                size: value,
                index: startIndex_1,
            });
            prevIndex = startIndex_1;
            prevOffset = aOffset;
            prevSize = value;
        }
    }
    catch (e_3_1) { e_3 = { error: e_3_1 }; }
    finally {
        try {
            if (_c && !_c.done && (_a = _b.return)) _a.call(_b);
        }
        finally { if (e_3) throw e_3.error; }
    }
    return {
        offsetTree: offsetTree,
        lastIndex: prevIndex,
        lastOffset: prevOffset,
        lastSize: prevSize,
    };
}
export function sizeStateReducer(state, _a) {
    var _b;
    var _c = __read(_a, 3), ranges = _c[0], groupIndices = _c[1], log = _c[2];
    if (ranges.length > 0) {
        log('received item sizes', ranges, LogLevel.DEBUG);
    }
    var sizeTree = state.sizeTree;
    var newSizeTree = sizeTree;
    var syncStart = 0;
    // We receive probe item results from a group probe,
    // which should always pass an item and a group
    // the results contain two ranges, which we consider to mean that groups and items have different size
    if (groupIndices.length > 0 && empty(sizeTree) && ranges.length === 2) {
        var groupSize_1 = ranges[0].size;
        var itemSize_1 = ranges[1].size;
        newSizeTree = groupIndices.reduce(function (tree, groupIndex) {
            return insert(insert(tree, groupIndex, groupSize_1), groupIndex + 1, itemSize_1);
        }, newSizeTree);
    }
    else {
        _b = __read(insertRanges(newSizeTree, ranges), 2), newSizeTree = _b[0], syncStart = _b[1];
    }
    if (newSizeTree === sizeTree) {
        return state;
    }
    var _d = createOffsetTree(state.offsetTree, syncStart, newSizeTree), newOffsetTree = _d.offsetTree, lastIndex = _d.lastIndex, lastSize = _d.lastSize, lastOffset = _d.lastOffset;
    return {
        sizeTree: newSizeTree,
        offsetTree: newOffsetTree,
        lastIndex: lastIndex,
        lastOffset: lastOffset,
        lastSize: lastSize,
        groupOffsetTree: groupIndices.reduce(function (tree, index) {
            return insert(tree, index, offsetOf(index, newOffsetTree));
        }, newTree()),
        groupIndices: groupIndices,
    };
}
export function offsetOf(index, tree) {
    if (tree.length === 0) {
        return 0;
    }
    var _a = arrayBinarySearch.findClosestSmallerOrEqual(tree, index, indexComparator), offset = _a.offset, startIndex = _a.index, size = _a.size;
    return size * (index - startIndex) + offset;
}
export function originalIndexFromItemIndex(itemIndex, sizes) {
    if (!hasGroups(sizes)) {
        return itemIndex;
    }
    var groupOffset = 0;
    while (sizes.groupIndices[groupOffset] <= itemIndex + groupOffset) {
        groupOffset++;
    }
    // we find the real item index, offsetting it by the number of group items before it
    return itemIndex + groupOffset;
}
export function hasGroups(sizes) {
    return !empty(sizes.groupOffsetTree);
}
var SIZE_MAP = {
    offsetHeight: 'height',
    offsetWidth: 'width',
};
export var sizeSystem = u.system(function (_a) {
    var _b = __read(_a, 1), log = _b[0].log;
    var sizeRanges = u.stream();
    var totalCount = u.stream();
    var statefulTotalCount = u.statefulStreamFromEmitter(totalCount, 0);
    var unshiftWith = u.stream();
    var shiftWith = u.stream();
    var firstItemIndex = u.statefulStream(0);
    var groupIndices = u.statefulStream([]);
    var fixedItemSize = u.statefulStream(undefined);
    var defaultItemSize = u.statefulStream(undefined);
    var itemSize = u.statefulStream(function (el, field) {
        return correctItemSize(el, SIZE_MAP[field]);
    });
    var data = u.statefulStream(undefined);
    var initial = initialSizeState();
    var sizes = u.statefulStreamFromEmitter(u.pipe(sizeRanges, u.withLatestFrom(groupIndices, log), u.scan(sizeStateReducer, initial), u.distinctUntilChanged()), initial);
    u.connect(u.pipe(groupIndices, u.filter(function (indexes) { return indexes.length > 0; }), u.withLatestFrom(sizes), u.map(function (_a) {
        var _b = __read(_a, 2), groupIndices = _b[0], sizes = _b[1];
        var groupOffsetTree = groupIndices.reduce(function (tree, index, idx) {
            return insert(tree, index, offsetOf(index, sizes.offsetTree) || idx);
        }, newTree());
        return __assign(__assign({}, sizes), { groupIndices: groupIndices,
            groupOffsetTree: groupOffsetTree });
    })), sizes);
    // decreasing the total count should remove any existing entries
    // beyond the last index - do this by publishing the default size as a range over them.
    u.connect(u.pipe(totalCount, u.withLatestFrom(sizes), u.filter(function (_a) {
        var _b = __read(_a, 2), totalCount = _b[0], lastIndex = _b[1].lastIndex;
        return totalCount < lastIndex;
    }), u.map(function (_a) {
        var _b = __read(_a, 2), totalCount = _b[0], _c = _b[1], lastIndex = _c.lastIndex, lastSize = _c.lastSize;
        return [
            {
                startIndex: totalCount,
                endIndex: lastIndex,
                size: lastSize,
            },
        ];
    })), sizeRanges);
    u.connect(fixedItemSize, defaultItemSize);
    var trackItemSizes = u.statefulStreamFromEmitter(u.pipe(fixedItemSize, u.map(function (size) { return size === undefined; })), true);
    u.connect(u.pipe(defaultItemSize, u.filter(function (value) {
        return value !== undefined && empty(u.getValue(sizes).sizeTree);
    }), u.map(function (size) { return [{ startIndex: 0, endIndex: 0, size: size }]; })), sizeRanges);
    var listRefresh = u.streamFromEmitter(u.pipe(sizeRanges, u.withLatestFrom(sizes), u.scan(function (_a, _b) {
        var oldSizes = _a.sizes;
        var _c = __read(_b, 2), newSizes = _c[1];
        return {
            changed: newSizes !== oldSizes,
            sizes: newSizes,
        };
    }, { changed: false, sizes: initial }), u.map(function (value) { return value.changed; })));
    u.subscribe(u.pipe(firstItemIndex, u.scan(function (prev, next) {
        return { diff: prev.prev - next, prev: next };
    }, { diff: 0, prev: 0 }), u.map(function (val) { return val.diff; })), function (offset) {
        if (offset > 0) {
            u.publish(unshiftWith, offset);
        }
        else if (offset < 0) {
            u.publish(shiftWith, offset);
        }
    });
    u.subscribe(u.pipe(firstItemIndex, u.withLatestFrom(log)), function (_a) {
        var _b = __read(_a, 2), index = _b[0], log = _b[1];
        if (index < 0) {
            log("`firstItemIndex` prop should not be set to less than zero. If you don't know the total count, just use a very high value", { firstItemIndex: firstItemIndex }, LogLevel.ERROR);
        }
    });
    // Capture the current list top item before the sizes get refreshed
    var beforeUnshiftWith = u.streamFromEmitter(unshiftWith);
    u.connect(u.pipe(unshiftWith, u.withLatestFrom(sizes), u.map(function (_a) {
        var _b = __read(_a, 2), unshiftWith = _b[0], sizes = _b[1];
        if (sizes.groupIndices.length > 0) {
            throw new Error('Virtuoso: prepending items does not work with groups');
        }
        return walk(sizes.sizeTree).reduce(function (acc, _a) {
            var index = _a.k, size = _a.v;
            return {
                ranges: __spread(acc.ranges, [
                    {
                        startIndex: acc.prevIndex,
                        endIndex: index + unshiftWith - 1,
                        size: acc.prevSize,
                    },
                ]),
                prevIndex: index + unshiftWith,
                prevSize: size,
            };
        }, {
            ranges: [],
            prevIndex: 0,
            prevSize: sizes.lastSize,
        }).ranges;
    })), sizeRanges);
    var shiftWithOffset = u.streamFromEmitter(u.pipe(shiftWith, u.withLatestFrom(sizes), u.map(function (_a) {
        var _b = __read(_a, 2), shiftWith = _b[0], offsetTree = _b[1].offsetTree;
        var newFirstItemIndex = -shiftWith;
        return offsetOf(newFirstItemIndex, offsetTree);
    })));
    u.connect(u.pipe(shiftWith, u.withLatestFrom(sizes), u.map(function (_a) {
        var _b = __read(_a, 2), shiftWith = _b[0], sizes = _b[1];
        if (sizes.groupIndices.length > 0) {
            throw new Error('Virtuoso: shifting items does not work with groups');
        }
        var newSizeTree = walk(sizes.sizeTree).reduce(function (acc, _a) {
            var k = _a.k, v = _a.v;
            return insert(acc, Math.max(0, k + shiftWith), v);
        }, newTree());
        return __assign(__assign(__assign({}, sizes), { sizeTree: newSizeTree }), createOffsetTree(sizes.offsetTree, 0, newSizeTree));
    })), sizes);
    return {
        // input
        data: data,
        totalCount: totalCount,
        sizeRanges: sizeRanges,
        groupIndices: groupIndices,
        defaultItemSize: defaultItemSize,
        fixedItemSize: fixedItemSize,
        unshiftWith: unshiftWith,
        shiftWith: shiftWith,
        shiftWithOffset: shiftWithOffset,
        beforeUnshiftWith: beforeUnshiftWith,
        firstItemIndex: firstItemIndex,
        // output
        sizes: sizes,
        listRefresh: listRefresh,
        statefulTotalCount: statefulTotalCount,
        trackItemSizes: trackItemSizes,
        itemSize: itemSize,
    };
}, u.tup(loggerSystem), { singleton: true });
